import type { ConfiguredProductInput, PublicOptionGroup, PublicProduct } from "../../../shared/types";
import { productRequiresVisualConfiguration } from "../../../shared/product-configurability";
import type { CartProductConfiguration } from "../../contexts/CartContext";

export function effectiveBasePrice(product: PublicProduct, base: number): number {
  if (product.promoType === "percentage")
    return Math.max(0, Math.round(base * (1 - product.promoValue / 100)));
  if (product.promoType === "fixed")
    return Math.max(0, Math.round(product.promoValue));
  return base;
}

export function productDisplayPrice(
  product: PublicProduct,
  branchId: string,
): { cents: number; from: boolean } {
  if (product.priceMode !== "variante")
    return { cents: product.effectivePriceUsdCents, from: false };
  const prices = product.variants
    .filter(
      (variant) =>
        !branchId || !variant.unavailableBranchIds.includes(branchId),
    )
    .map((variant) => effectiveBasePrice(product, variant.priceUsdCents));
  return { cents: prices.length ? Math.min(...prices) : 0, from: true };
}

export function availableProduct(
  product: PublicProduct | undefined,
  branchId: string,
): product is PublicProduct {
  return Boolean(
    product &&
    product.type !== "combo" &&
    (!branchId || !product.unavailableBranchIds.includes(branchId)) &&
    (product.priceMode !== "variante" ||
      product.variants.some(
        (variant) =>
          !branchId || !variant.unavailableBranchIds.includes(branchId),
      )),
  );
}

export function productCanBeConfigured(
  product: PublicProduct,
  products: ReadonlyMap<string, PublicProduct>,
  branchId: string,
): boolean {
  if (branchId && product.unavailableBranchIds.includes(branchId)) return false;
  if (
    product.priceMode === "variante" &&
    !product.variants.some(
      (variant) =>
        !branchId || !variant.unavailableBranchIds.includes(branchId),
    )
  )
    return false;
  return (
    product.type !== "combo" ||
    product.comboComponents.every((component) =>
      component.products.some((choice) =>
        availableProduct(products.get(choice.productId), branchId),
      ),
    )
  );
}

export function createProductConfiguration(
  product: PublicProduct,
  branchId: string,
): ConfiguredProductInput {
  const variants = product.variants.filter(
    (variant) => !branchId || !variant.unavailableBranchIds.includes(branchId),
  );
  return {
    productId: product.id,
    variantId:
      product.priceMode === "variante" && variants.length === 1
        ? variants[0].id
        : null,
    groups: [],
    excludedIngredientIds: [],
    includedIngredientIds: [],
  };
}

export function createCartConfiguration(
  product: PublicProduct,
  products: ReadonlyMap<string, PublicProduct>,
  branchId: string,
): CartProductConfiguration {
  const comboComponents =
    product.type === "combo"
      ? product.comboComponents.map((component) => {
          const choices = component.products.filter((choice) =>
            availableProduct(products.get(choice.productId), branchId),
          );
          const choice = choices.find((item) => item.default) ?? choices[0];
          const componentProduct = choice
            ? products.get(choice.productId)
            : undefined;
          return {
            componentId: component.id,
            units: componentProduct
              ? Array.from({ length: component.quantity }, () =>
                  createProductConfiguration(componentProduct, branchId),
                )
              : [],
          };
        })
      : [];
  return { ...createProductConfiguration(product, branchId), comboComponents };
}

export function cloneConfiguration(
  configuration: CartProductConfiguration,
): CartProductConfiguration {
  return {
    ...configuration,
    groups: configuration.groups.map((group) => ({
      groupId: group.groupId,
      options: group.options.map((option) => ({ ...option })),
    })),
    excludedIngredientIds: [...configuration.excludedIngredientIds],
    includedIngredientIds: [...configuration.includedIngredientIds],
    comboComponents: configuration.comboComponents.map((component) => ({
      componentId: component.componentId,
      units: component.units.map((unit) => ({
        ...unit,
        groups: unit.groups.map((group) => ({
          groupId: group.groupId,
          options: group.options.map((option) => ({ ...option })),
        })),
        excludedIngredientIds: [...unit.excludedIngredientIds],
        includedIngredientIds: [...unit.includedIngredientIds],
      })),
    })),
  };
}

export function selectionRule(group: PublicOptionGroup): string {
  const min = Math.max(group.required ? 1 : 0, group.minSelections);
  if (min === group.maxSelections) return `Elige exactamente ${min}.`;
  if (min === 0) return `Elige hasta ${group.maxSelections}.`;
  return `Elige entre ${min} y ${group.maxSelections}.`;
}

export function needsSteps(product: PublicProduct): boolean {
  return productRequiresVisualConfiguration(product);
}
